import type { CSSProperties, HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { TABLET_BREAKPOINT } from '../../../../styles/breakpoints';
import { colors } from '../../../../styles/colors';
import { fontFamily, headingLevel, maxOnboardingContentWidth, space } from '../../../../styles/onboardingUiTokens';

/** Matches `BouncyScrollView` welcome header gradient (bottom-left → top-right). */
const HERO_IMAGE_GRADIENT = 'linear-gradient(to top right, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0))';

export type HeroBannerTitleElement = 'h1' | 'h2' | 'h3' | 'div';

export type HeroBannerProps = {
  /** Image URL (`<img src>`). */
  imageSrc: string;
  /**
   * Optional responsive sources (e.g. `1x, 2x, 3x` from RN-style `@2x` / `@3x` exports).
   * Passed to `<img srcSet>`; keep `imageSrc` as the 1x fallback.
   */
  imageSrcSet?: string;
  /** Passed to `<img sizes>` when using width-based `srcSet`; optional for density-only `x` descriptors. */
  imageSizes?: string;
  /** AVIF encoding of the same photo, offered before the `imageSrcSet` baseline. */
  imageAvifSrcSet?: string;
  /**
   * Set on the first screen of a flow that shows this hero: it is the LCP element there, and the
   * default heuristics deprioritise it because it starts outside the viewport on short screens.
   */
  imagePriority?: boolean;
  /** When the image is decorative only, set true (default) and omit a visible caption. */
  decorativeImage?: boolean;
  /** Meaningful description when the image is informative; ignored when `decorativeImage` is true. */
  imageAlt?: string;
  /** Small line above the title (native `heroBannerSubText` / theme `h4`). */
  subText?: string;
  /** Primary headline (native `heroBannerText`). */
  title?: string;
  /** Semantic wrapper for `title`; use `h1` when this hero is the page’s main heading. */
  titleAs?: HeroBannerTitleElement;
  /** Minimum height of the banner (native welcome uses ~320 for the header image). */
  minHeight?: CSSProperties['minHeight'];
  /** `object-position` for `object-fit: cover` (e.g. `center top`). */
  imageObjectPosition?: CSSProperties['objectPosition'];
  className?: string;
  style?: CSSProperties;
} & Omit<HTMLAttributes<HTMLDivElement>, 'title'>;

const Root = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  background: ${colors.dark};
`;

/** `contents` so the wrapper adds no box: `Media` keeps `Root` as its positioning container. */
const Picture = styled.picture`
  display: contents;
`;

const Media = styled.img`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Gradient = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: ${HERO_IMAGE_GRADIENT};
`;

const TextBlock = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  box-sizing: border-box;
  padding: ${space.lg} ${space.lg} ${space.lg};
`;

const SubText = styled.p`
  margin: 0;
  color: ${colors.white};
  font-family: ${headingLevel[4].fontFamily};
  font-weight: ${headingLevel[4].fontWeight};
  letter-spacing: ${headingLevel[4].letterSpacing};
  font-size: ${headingLevel[4].fontSize};
  line-height: ${headingLevel[4].lineHeight};
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const titleCss = css`
  margin: 0;
  margin-top: ${space.xxs};
  color: ${colors.white};
  font-family: ${fontFamily.ubuntu};
  font-weight: 700;
  font-size: ${headingLevel[1].fontSize};
  line-height: ${headingLevel[1].lineHeight};
  max-width: 100%;
`;

const Title = styled.h1<{ $singleLine: boolean }>`
  ${titleCss}
  ${({ $singleLine }) =>
    $singleLine
      ? css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : undefined}
`;

/**
 * Full-width hero with cover image, bottom-left headline stack, and the same diagonal
 * fade as the patient app welcome header (`BouncyScrollView`).
 */
export function HeroBanner({
  imageSrc,
  imageSrcSet,
  imageSizes,
  imageAvifSrcSet,
  imagePriority = false,
  decorativeImage = true,
  imageAlt,
  subText,
  title,
  titleAs = 'h1',
  minHeight = 'calc(4rem + 20dvh)',
  imageObjectPosition = 'center 20%',
  className,
  style,
  ...rest
}: HeroBannerProps) {
  const resolvedAlt = decorativeImage ? '' : (imageAlt ?? '');
  const imgAriaHidden = decorativeImage;

  const showText = Boolean(subText || title);
  const titleSingleLine = Boolean(subText && title);

  return (
    <Root className={className} style={{ minHeight, ...style }} {...rest}>
      <Picture>
        {imageAvifSrcSet ? <source type="image/avif" srcSet={imageAvifSrcSet} sizes={imageSizes} /> : null}
        <Media
          src={imageSrc}
          srcSet={imageSrcSet}
          sizes={imageSizes}
          alt={resolvedAlt}
          aria-hidden={imgAriaHidden || undefined}
          decoding="async"
          fetchPriority={imagePriority ? 'high' : undefined}
          draggable={false}
          style={{ objectPosition: imageObjectPosition }}
        />
      </Picture>
      <Gradient aria-hidden />
      {showText ? (
        <TextBlock>
          {subText ? <SubText>{subText}</SubText> : null}
          {title ? (
            <Title as={titleAs} $singleLine={titleSingleLine}>
              {title}
            </Title>
          ) : null}
        </TextBlock>
      ) : null}
    </Root>
  );
}

/** Framed hero used inside checkout-style content columns: centered, capped width and rounded from `TABLET_BREAKPOINT` up. */
export const FramedHeroBanner = styled(HeroBanner)`
  margin: 0 auto;
  @media (min-width: ${TABLET_BREAKPOINT}) {
    max-width: ${maxOnboardingContentWidth};
    border-radius: 8px;
    margin-top: ${space.lg};
  }
`;
